/**
 * <delivery-countdown> — counts down to a daily next-day-delivery cut-off time.
 *
 * Attributes:
 *   data-cutoff     — cut-off time in 24h "HH:MM" (in the market's local time)
 *   data-timezone   — IANA timezone the cut-off is defined in (e.g. "Europe/London")
 *   data-weekend-enabled — "true" to apply the no-Sunday-delivery weekend rule:
 *                     from Friday's cut-off through Sunday every order lands on
 *                     Monday, so hide the countdown and show the static
 *                     data-text-weekend message instead.
 *
 * Expects a descendant with [data-countdown-time] to write "HH:MM:SS" into.
 *
 * The timer always runs to the next cut-off — it never resets to midnight — but
 * the copy changes with what the shopper is actually buying. Before the cut-off
 * today's dispatch is still open, the order lands tomorrow, and the "before" copy
 * shows (data-prefix-before / data-suffix-before). Once the cut-off passes the
 * order goes out on the next dispatch day and lands the day after that, so the
 * "after" copy shows (data-prefix-after / data-suffix-after) — those can name the
 * day with a [day] placeholder, e.g. "for delivery Wednesday". At midnight the
 * cut-off is ahead again and the "before" copy comes back on its own.
 *
 * The weekend rule (when enabled) takes precedence over both: DPD doesn't deliver
 * on Sundays, so orders placed from Friday's cut-off until Monday all arrive Monday.
 */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

class DeliveryCountdown extends HTMLElement {
  connectedCallback() {
    const [h, m] = (this.dataset.cutoff || '16:00').split(':');
    this.cutoffSeconds = (parseInt(h, 10) || 0) * 3600 + (parseInt(m, 10) || 0) * 60;
    this.timezone = this.dataset.timezone || undefined;
    this.weekendEnabled = this.dataset.weekendEnabled === 'true';
    this.locale = document.documentElement.lang || 'en-GB';
    this.output = this.querySelector('[data-countdown-time]');
    this.prefixEl = this.querySelector('[data-countdown-prefix]');
    this.suffixEl = this.querySelector('[data-countdown-suffix]');
    if (!this.output) return;

    this.update = this.update.bind(this);
    this.update();
    this.timer = setInterval(this.update, 1000);
  }

  /**
   * Keep an element's text in sync without clobbering it every tick.
   * @param {Element | null | undefined} el
   * @param {string | undefined} value
   */
  setText(el, value) {
    const text = value || '';
    if (el && el.textContent !== text) el.textContent = text;
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  // Current wall-clock time of the target timezone: seconds since midnight, the
  // weekday (0 = Sunday … 6 = Saturday), and that day's calendar date anchored to
  // UTC midnight so day arithmetic can't be dragged around by DST.
  now() {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timezone,
        hour12: false,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).formatToParts(new Date());
      /** @param {string} type */
      const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
      let hours = parseInt(get('hour'), 10);
      if (hours === 24) hours = 0; // some engines emit "24" at midnight
      const seconds = hours * 3600 + parseInt(get('minute'), 10) * 60 + parseInt(get('second'), 10);
      /** @type {Record<string, number>} */
      const weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      const weekday = weekdays[get('weekday')] ?? new Date().getDay();
      const date = new Date(
        Date.UTC(parseInt(get('year'), 10), parseInt(get('month'), 10) - 1, parseInt(get('day'), 10))
      );
      return { seconds, weekday, date };
    } catch (e) {
      const d = new Date();
      return {
        seconds: d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds(),
        weekday: d.getDay(),
        date: new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())),
      };
    }
  }

  /**
   * Name of the day an order placed now arrives on, `offsetDays` after today.
   * DPD doesn't deliver on Sundays, so anything landing on one slips to Monday.
   * @param {Date} today  today's date in the target timezone, anchored to UTC midnight
   * @param {number} offsetDays
   */
  deliveryDayName(today, offsetDays) {
    const delivery = new Date(today.getTime());
    delivery.setUTCDate(delivery.getUTCDate() + offsetDays);
    if (delivery.getUTCDay() === 0) delivery.setUTCDate(delivery.getUTCDate() + 1);
    try {
      return new Intl.DateTimeFormat(this.locale, { weekday: 'long', timeZone: 'UTC' }).format(delivery);
    } catch (e) {
      return DAY_NAMES[delivery.getUTCDay()];
    }
  }

  update() {
    if (!this.output) return;
    const { seconds: now, weekday, date } = this.now();
    const afterCutoff = now >= this.cutoffSeconds;

    // No Sunday delivery: Friday after cut-off, plus all of Saturday and Sunday,
    // every order arrives Monday. There's no meaningful same-day deadline during
    // this window (any order lands Monday), so show a static message with no timer.
    const inWeekendWindow =
      this.weekendEnabled && ((weekday === 5 && afterCutoff) || weekday === 6 || weekday === 0);

    if (inWeekendWindow) {
      this.setText(this.prefixEl, this.dataset.textWeekend);
      this.setText(this.suffixEl, '');
      this.output.toggleAttribute('hidden', true);
      return;
    }
    this.output.toggleAttribute('hidden', false);

    // Always count to the next cut-off. Before it that's later today; after it
    // that's tomorrow's, so the timer rolls on rather than resetting at midnight.
    const remaining = afterCutoff ? 86400 - now + this.cutoffSeconds : this.cutoffSeconds - now;

    // Ordering before the cut-off makes today's dispatch, so delivery is tomorrow.
    // Ordering after it misses today's, so dispatch is tomorrow and delivery the
    // day after — which is why the after copy names the day instead of promising
    // next day.
    let prefix = afterCutoff ? this.dataset.prefixAfter : this.dataset.prefixBefore;
    let suffix = afterCutoff ? this.dataset.suffixAfter : this.dataset.suffixBefore;

    if (/\[day\]/i.test(`${prefix || ''}${suffix || ''}`)) {
      const day = this.deliveryDayName(date, afterCutoff ? 2 : 1);
      prefix = (prefix || '').replace(/\[day\]/gi, day);
      suffix = (suffix || '').replace(/\[day\]/gi, day);
    }

    this.setText(this.prefixEl, prefix);
    this.setText(this.suffixEl, suffix);

    const hh = String(Math.floor(remaining / 3600)).padStart(2, '0');
    const mm = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');
    this.output.textContent = `${hh}:${mm}:${ss}`;
  }
}

if (!customElements.get('delivery-countdown')) {
  customElements.define('delivery-countdown', DeliveryCountdown);
}
